import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, FileArchive, Loader2, PackageOpen, ExternalLink, ChevronRight, Mail, Send } from "lucide-react";
import { apiGet, apiPost, openDocument } from "@/lib/api";

const DOCS_FABRICA_1 = [{ tip: "manopere-cumulat", label: "Manopere cumulat" }];
const DOCS_FABRICA_2 = [{ tip: "manopere-f2", label: "Manopere F2" }];

const fmtData = (iso) => new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });

const EmailButton = ({ comanda, fabrica }) => {
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState((fabrica === "2" ? comanda.manopere_email_f2 : comanda.manopere_email) || null);
  const hasErp = !!(comanda.com_erp || "").trim();
  const send = async () => {
    setSending(true);
    try {
      const r = await apiPost(`/comenzi/${comanda.id}/manopere-email?fabrica=${fabrica}`, {});
      setLast(r);
      toast.success(`Trimis catre ${r.to} (CC ${r.cc})`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <Button
        data-testid={`manopere-btn-trimite-email-f${fabrica}`}
        disabled={!hasErp || sending}
        onClick={send}
        className="justify-between h-auto py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white"
      >
        <span className="flex items-center gap-2 font-medium"><Mail className="h-4 w-4 text-amber-400" />Trimite pe mail</span>
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
      <span className="text-xs text-slate-500" data-testid={`manopere-email-status-f${fabrica}`}>
        {!hasErp
          ? "Seteaza Nr. com ERP in ecranul Comenzi pentru a trimite."
          : last
            ? `Ultima trimitere: ${fmtData(last.trimis_la)} (com. ${last.com_erp})`
            : `Netrimis. Subiect: ${fabrica === "2" ? "Manopere F2" : "Manopere"} com. ${comanda.com_erp} (+ Prod_0_consum_insumat)`}
      </span>
    </div>
  );
};

const DocButtons = ({ comanda, docs, fabrica }) => {
  const [opening, setOpening] = useState(null);
  const open = async (tip) => {
    setOpening(tip);
    try {
      await openDocument(comanda.id, tip);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOpening(null);
    }
  };
  return (
    <Card className="mt-2 mb-1 p-4 border-amber-200 bg-white" data-testid="manopere-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {docs.map((doc) => (
          <Button
            key={doc.tip}
            variant="outline"
            data-testid={`manopere-btn-${doc.tip}`}
            disabled={opening === doc.tip}
            onClick={() => open(doc.tip)}
            className="justify-between h-auto py-3 px-4 text-left border-slate-200 hover:border-amber-400 hover:bg-amber-50"
          >
            <span className="flex items-center gap-2 font-medium text-slate-800">
              <FileText className="h-4 w-4 text-amber-600" />
              {doc.label}
            </span>
            {opening === doc.tip
              ? <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
              : <ExternalLink className="h-4 w-4 text-slate-400" />}
          </Button>
        ))}
        <EmailButton comanda={comanda} fabrica={fabrica} />
      </div>
    </Card>
  );
};

const ComenziList = ({ comenzi, selected, onSelect, docs, fabrica }) => (
  <div className="space-y-2" data-testid="manopere-comenzi-list">
    {comenzi.map((c) => {
      const isOpen = selected?.id === c.id;
      return (
        <div key={c.id} id={`comanda-${c.id}`}>
          <Card
            data-testid={`manopere-comanda-${c.nr_comanda}`}
            onClick={() => onSelect(isOpen ? null : c)}
            className={`p-3 cursor-pointer transition-all flex items-center gap-3 ${
              isOpen ? "border-amber-400 bg-amber-50 shadow-sm" : "hover:border-amber-300 hover:shadow-sm"
            }`}
          >
            <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
              <FileArchive className="h-4.5 w-4.5 text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900 truncate text-sm">{c.nr_comanda}</div>
              <div className="text-xs text-slate-500 truncate">
                {c.client_nume || "(fara client)"} · {c.nr_pozitii} pozitii
              </div>
            </div>
            <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-90 text-amber-500" : "text-slate-300"}`} />
          </Card>
          {isOpen && <DocButtons comanda={c} docs={docs} fabrica={fabrica} />}
        </div>
      );
    })}
  </div>
);

const FabricaPanel = ({ docs, fabrica }) => {
  const [comenzi, setComenzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    apiGet("/comenzi")
      .then((d) => setComenzi(d.items || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const [sp] = useSearchParams();
  const focusId = sp.get("comanda");
  useEffect(() => {
    const c = comenzi.find((x) => x.id === focusId);
    if (!c) return;
    setSelected(c);
    setTimeout(() => document.getElementById(`comanda-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [focusId, comenzi]);

  if (loading) return <div className="py-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (comenzi.length === 0) {
    return (
      <Card className="py-14 flex flex-col items-center text-slate-400" data-testid="manopere-empty">
        <PackageOpen className="h-9 w-9 mb-3" />
        <p className="text-sm">Nicio comanda importata.</p>
      </Card>
    );
  }
  return <ComenziList comenzi={comenzi} selected={selected} onSelect={setSelected} docs={docs} fabrica={fabrica} />;
};

export default function ManoperePage() {
  return (
    <div data-testid="manopere-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Manopere</h1>
        <p className="text-sm text-slate-500 mt-1">Alege fabrica si comanda, apoi deschide raportul de manopere.</p>
      </div>
      <Tabs defaultValue="fabrica-1">
        <TabsList className="mb-4">
          <TabsTrigger value="fabrica-1" data-testid="manopere-tab-fabrica-1">Fabrica-1</TabsTrigger>
          <TabsTrigger value="fabrica-2" data-testid="manopere-tab-fabrica-2">Fabrica-2</TabsTrigger>
        </TabsList>
        <TabsContent value="fabrica-1"><FabricaPanel docs={DOCS_FABRICA_1} fabrica="1" /></TabsContent>
        <TabsContent value="fabrica-2"><FabricaPanel docs={DOCS_FABRICA_2} fabrica="2" /></TabsContent>
      </Tabs>
    </div>
  );
}
